import { Box, Chip, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material';
import type { OverallRanking } from '../../types/results';
import type { Discipline } from '../../types/domain';
import { RankBadge } from './RankBadge';

interface OverallRankingTableProps {
  ranking: OverallRanking;
  disciplines: Discipline[];
  highlightTeamId?: number | null;
  showStartNumber?: boolean;
  showPoints?: boolean;
}

export const OverallRankingTable = ({
  ranking,
  disciplines,
  highlightTeamId,
  showStartNumber = true,
  showPoints = true,
}: OverallRankingTableProps) => {
  const baseCols = 2 + (showStartNumber ? 1 : 0) + (showPoints ? 1 : 0);
  const totalCols = baseCols + disciplines.length;
  return (
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 600, width: 80 }}>Umístění</TableCell>
            {showStartNumber && <TableCell sx={{ fontWeight: 600, width: 60 }}>Č.</TableCell>}
            <TableCell sx={{ fontWeight: 600 }}>Družstvo</TableCell>
            {disciplines.map((d) => (
              <TableCell key={d.id} align="center" sx={{ fontWeight: 600, fontSize: '0.75rem' }}>
                {d.name}
              </TableCell>
            ))}
            {showPoints && (
              <TableCell align="right" sx={{ fontWeight: 700 }}>
                Body
              </TableCell>
            )}
          </TableRow>
        </TableHead>
        <TableBody>
          {ranking.teams.map((t) => {
            const isHighlight = highlightTeamId === t.teamId;
            return (
              <TableRow
                key={t.teamId}
                sx={{
                  bgcolor: isHighlight ? 'primary.light' : undefined,
                  '&:hover': { bgcolor: 'action.hover' },
                }}
              >
                <TableCell>
                  <Stack direction="row" spacing={0.75} alignItems="center">
                    <RankBadge rank={t.finalRank} size={28} />
                  </Stack>
                </TableCell>
                {showStartNumber && <TableCell>{t.startNumber}</TableCell>}
                <TableCell>
                  <Typography fontWeight={isHighlight ? 700 : 500}>{t.teamName}</Typography>
                </TableCell>
                {disciplines.map((d) => {
                  const r = t.perDisciplineRanks[d.id];
                  return (
                    <TableCell key={d.id} align="center">
                      <Chip size="small" label={r ?? '—'} variant="outlined" />
                    </TableCell>
                  );
                })}
                {showPoints && (
                  <TableCell align="right">
                    <Typography fontFamily="monospace" fontWeight={700} color="primary.main">
                      {t.totalPoints}
                    </Typography>
                  </TableCell>
                )}
              </TableRow>
            );
          })}
          {ranking.teams.length === 0 && (
            <TableRow>
              <TableCell colSpan={totalCols}>
                <Box sx={{ textAlign: 'center', py: 3, color: 'text.secondary' }}>
                  Žádná družstva v této kategorii.
                </Box>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
};
